import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { journeyTracker } from '../lib/journeyTracker';
import { outcomeTracker } from '../lib/outcomeTracker';

interface WizardContextType {
  isWizardEnabled: boolean;
  wizardState: {
    isVisible: boolean;
    isExpanded: boolean;
    isGuiding: boolean;
    currentStep: number;
    totalSteps: number;
  };
  wizardContext: {
    currentPage: string;
    userJourney: any;
    visitedPages: string[];
    outcomes: any[];
    lastActivity: Date;
  };
  toggleWizard: () => void;
  setWizardState: (state: Partial<WizardContextType['wizardState']>) => void;
  updateContext: () => void;
}

const WizardContext = createContext<WizardContextType | undefined>(undefined);

interface WizardProviderProps {
  children: ReactNode;
}

export const WizardProvider: React.FC<WizardProviderProps> = ({ children }) => {
  const location = useLocation();
  const [isWizardEnabled, setIsWizardEnabled] = useState(true);
  const [wizardState, setWizardState] = useState({
    isVisible: true,
    isExpanded: false,
    isGuiding: false,
    currentStep: 0,
    totalSteps: 0
  });
  
  const [wizardContext, setWizardContext] = useState({
    currentPage: '',
    userJourney: null,
    visitedPages: [],
    outcomes: [],
    lastActivity: new Date()
  });

  // Update context when location changes
  const updateContext = () => {
    const journey = journeyTracker.getJourney();
    const outcomes = outcomeTracker.getOutcomes();
    const visitedPages = journey.visitedPages?.map((p: any) => p.path) || [];
    
    setWizardContext({
      currentPage: location.pathname,
      userJourney: journey,
      visitedPages,
      outcomes,
      lastActivity: new Date()
    });
  };

  useEffect(() => {
    updateContext();
  }, [location.pathname]);

  const toggleWizard = () => {
    setIsWizardEnabled(!isWizardEnabled);
  };

  const handleSetWizardState = (newState: Partial<WizardContextType['wizardState']>) => {
    setWizardState(prev => ({ ...prev, ...newState }));
  };

  const value: WizardContextType = {
    isWizardEnabled,
    wizardState,
    wizardContext,
    toggleWizard,
    setWizardState: handleSetWizardState,
    updateContext
  };

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
};

export const useWizard = (): WizardContextType => {
  const context = useContext(WizardContext);
  if (context === undefined) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};
